import React, { useEffect, useState, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapOverlay.css';
import { useMessages } from '../../../../i18n/I18n';

interface MapOverlayProps {
  imageUrl: string;
  onBoundsChanged: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  initialBounds: {
    top_left: { latitude: number; longitude: number };
    bottom_right: { latitude: number; longitude: number };
  };
}

interface GenericBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Where the plan is drawn in the map's container, in pixels: the image is scaled to fit the container
 * (object-fit: contain) and centered. Under a pixel of difference, the plan fills the container.
 */
const drawnPlan = (containerWidth: number, containerHeight: number, plan: { width: number; height: number }) => {
  const scale = Math.min(containerWidth / plan.width, containerHeight / plan.height);
  const width = containerWidth - plan.width * scale < 1 ? containerWidth : plan.width * scale;
  const height = containerHeight - plan.height * scale < 1 ? containerHeight : plan.height * scale;
  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
    fillsContainer: width === containerWidth && height === containerHeight,
  };
};

// Custom zoom control component
const ZoomControl: React.FC<{
  zoom: number;
  onZoomChange: (zoom: number) => void;
}> = ({ zoom, onZoomChange }) => {
  const t = useMessages('maps');
  const handleZoomIn = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (zoom < 22) {
      const newZoom = Math.min(22, parseFloat((zoom + 0.1).toFixed(1)));
      onZoomChange(newZoom);
    }
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (zoom > 1) {
      const newZoom = Math.max(1, parseFloat((zoom - 0.1).toFixed(1)));
      onZoomChange(newZoom);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newZoom = parseFloat(parseFloat(e.target.value).toFixed(1));
    if (!isNaN(newZoom)) {
      onZoomChange(newZoom);
    }
  };

  return (
    <div className="custom-zoom-control">
      <div className="control-title">{t.zoomLevel}</div>
      <div className="control-content">
        <button 
          type="button" 
          onClick={handleZoomOut}
          disabled={zoom <= 1}
        >-</button>
        <input
          type="range"
          min="1"
          max="22"
          step="0.1"
          value={zoom}
          onChange={handleSliderChange}
        />
        <button 
          type="button" 
          onClick={handleZoomIn}
          disabled={zoom >= 22}
        >+</button>
        <div className="zoom-value">{zoom.toFixed(1)}</div>
      </div>
    </div>
  );
};

// Add new TransparencyControl component
const TransparencyControl: React.FC<{
  opacity: number;
  onOpacityChange: (opacity: number) => void;
}> = ({ opacity, onOpacityChange }) => {
  const t = useMessages('maps');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseFloat(e.target.value);
    onOpacityChange(newOpacity);
  };

  return (
    <div className="custom-transparency-control">
      <div className="control-title">{t.transparency}</div>
      <div className="control-content">
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={opacity}
          onChange={handleChange}
        />
        <span className="opacity-value">{Math.round(opacity * 100)}%</span>
      </div>
    </div>
  );
};

const MapOverlay: React.FC<MapOverlayProps> = ({ imageUrl, onBoundsChanged, initialBounds }) => {
  const t = useMessages('maps');
  const [isLoading, setIsLoading] = useState(true);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(15);
  const [opacity, setOpacity] = useState(0.7);
  const mapboxRef = useRef<mapboxgl.Map | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  /** The plan image's own size, known once it has loaded */
  const planSizeRef = useRef<{ width: number; height: number } | null>(null);

  const bounds: GenericBounds = {
    north: Number(initialBounds.top_left.latitude),
    south: Number(initialBounds.bottom_right.latitude),
    east: Number(initialBounds.bottom_right.longitude),
    west: Number(initialBounds.top_left.longitude),
  };

  const center = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };

  // Image loading effect
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      planSizeRef.current = img.naturalWidth > 0 && img.naturalHeight > 0
        ? { width: img.naturalWidth, height: img.naturalHeight }
        : null;
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        
        const imageAspectRatio = img.naturalWidth / img.naturalHeight;
        const containerAspectRatio = containerWidth / containerHeight;
        
        let width, height;
        
        if (imageAspectRatio > containerAspectRatio) {
          width = containerWidth;
          height = containerWidth / imageAspectRatio;
        } else {
          height = containerHeight;
          width = containerHeight * imageAspectRatio;
        }
        
        setDimensions({
          width: Math.floor(width),
          height: Math.floor(height)
        });
      }
      setIsLoading(false);
    };
    img.onerror = () => {
      console.error('Failed to load image');
      setIsLoading(false);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Map initialization effect
  useEffect(() => {
    if (!mapDivRef.current || isLoading) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
    
    const map = new mapboxgl.Map({
      container: mapDivRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [center.lng, center.lat],
      zoom: zoom,
      minZoom: 1,
      maxZoom: 22,
    });

    map.on('load', () => {
      // First mark the map as loaded
      setIsMapLoading(false);
    });

    mapboxRef.current = map;

    return () => {
      if (mapboxRef.current) {
        mapboxRef.current.remove();
        mapboxRef.current = null;
      }
      setIsInitialized(false);
      setIsMapLoading(true);
    };
  }, [isLoading]);

  // Separate effect for initializing map position
  useEffect(() => {
    if (isMapLoading || !mapboxRef.current || isInitialized) return;

    const map = mapboxRef.current;

    requestAnimationFrame(() => {
      map.resize();

      // The saved bounds are the plan's corners: they sit on the plan as drawn, whatever the container's shape
      const planInContainer = () => {
        const { clientWidth, clientHeight } = map.getContainer();
        return planSizeRef.current ? drawnPlan(clientWidth, clientHeight, planSizeRef.current) : null;
      };

      const plan = planInContainer();
      const { clientWidth, clientHeight } = map.getContainer();
      map.fitBounds([
        [bounds.west, bounds.south],
        [bounds.east, bounds.north]
      ], {
        padding: !plan || plan.fillsContainer ? 0 : {
          top: plan.top,
          bottom: clientHeight - plan.top - plan.height,
          left: plan.left,
          right: clientWidth - plan.left - plan.width,
        },
        animate: false,
        offset: [0, 0],
        maxZoom: 18
      });

      const layers = [
        'poi-label',
        'transit-station-label',
        'airport-label',
        'place-label',
        'building-label'
      ];

      layers.forEach(layer => {
        if (map.getLayer(layer)) {
          map.setLayoutProperty(layer, 'text-field', ['get', 'name']);
          map.setLayoutProperty(layer, 'text-size', 12);
          map.setLayoutProperty(layer, 'text-anchor', 'top');
          map.setLayoutProperty(layer, 'text-offset', [0, 0.5]);
          map.setLayoutProperty(layer, 'visibility', 'visible');
          map.setLayoutProperty(layer, 'icon-size', 1);
          map.setLayoutProperty(layer, 'icon-allow-overlap', true);

          map.setPaintProperty(layer, 'text-color', '#ffffff');
          map.setPaintProperty(layer, 'text-halo-color', '#000000');
          map.setPaintProperty(layer, 'text-halo-width', 1);
        }
      });

      const handleMoveEnd = () => {
        const drawn = planInContainer();
        if (!drawn || drawn.fillsContainer) {
          const mapBounds = map.getBounds();
          onBoundsChanged({
            north: mapBounds.getNorth(),
            south: mapBounds.getSouth(),
            east: mapBounds.getEast(),
            west: mapBounds.getWest()
          });
          return;
        }
        const topLeft = map.unproject([drawn.left, drawn.top]);
        const bottomRight = map.unproject([drawn.left + drawn.width, drawn.top + drawn.height]);
        onBoundsChanged({
          north: topLeft.lat,
          south: bottomRight.lat,
          east: bottomRight.lng,
          west: topLeft.lng
        });
      };

      const handleZoomEnd = () => {
        setZoom(map.getZoom());
      };

      // fitBounds without animation has already ended its move: from now on, every move is the user's
      setZoom(map.getZoom());
      map.on('moveend', handleMoveEnd);
      map.on('zoomend', handleZoomEnd);

      setIsInitialized(true);
    });
  }, [isMapLoading]);

  const handleZoomChange = (newZoom: number) => {
    if (mapboxRef.current) {
      mapboxRef.current.zoomTo(newZoom, {
        duration: 300
      });
    }
  };

  return (
    <div className="map-overlay-wrapper" style={{ height: '100%', width: '100%' }}>
      <div className="map-controls">
        <ZoomControl zoom={zoom} onZoomChange={handleZoomChange} />
        <TransparencyControl opacity={opacity} onOpacityChange={setOpacity} />
      </div>
      
      <div 
        ref={containerRef} 
        className="map-overlay-container"
        style={{ 
          height: 'calc(100% - 60px)',
          width: '100%',
        }}
      >
        {isLoading ? (
          <div className="map-loading">{t.loadingPlan}</div>
        ) : (
          <div 
            className="map-overlay"
            style={{ 
              width: '100%',
              height: '100%',
              position: 'relative',
            }} 
          >
            <img 
              ref={imageRef}
              src={imageUrl}
              alt={t.overlayAlt}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                zIndex: 1,
              }}
            />
            
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              opacity: opacity,
            }}>
              {isMapLoading && (
                <div className="map-loading">{t.initializingMap}</div>
              )}
              <div 
                ref={mapDivRef}
                style={{ 
                  width: '100%', 
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  visibility: isMapLoading ? 'hidden' : 'visible'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapOverlay; 